import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Factory, CheckCircle, AlertTriangle, RefreshCw, Package,
  ThumbsUp, ShieldAlert, Loader2, FileBarChart, History, ScanBarcode, X, Printer, Settings, ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import PrintMethodToggle from '@/components/print/PrintMethodToggle';
import { UsnPrinterSettingsDialog } from '@/components/print/UsnPrinterSettingsDialog';
import { useUsnStickerPrint } from '@/hooks/useUsnStickerPrint';
import { listJobOrders, getJobOrder, getBundleUsns, returnJobOrder } from '@/services/inhouseJobOrderApi';

const OPEN_STATUSES = ['OPEN', 'RECEIVING'];

const parseStaff = (text) =>
  Array.from(new Set((text || '').split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean)));

const isAdminRole = () => {
  let u = {};
  try { u = JSON.parse(localStorage.getItem('user') || '{}'); } catch { u = {}; }
  const r = (u.role || localStorage.getItem('role') || '').toString().trim().toLowerCase();
  return ['admin', 'super_admin', 'site admin', 'site_admin'].includes(r);
};

const JobWorkerReturn = () => {
  const navigate = useNavigate();
  const usnInputRef = useRef(null);
  const staffInputRef = useRef(null);
  // Same aligned USN-sticker pipeline as Folding Print / Segregation.
  const print = useUsnStickerPrint({ screenKey: 'inhouse-job-order' });

  const [orders, setOrders] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState(null);
  const [returns, setReturns] = useState([]);
  const [sentGroups, setSentGroups] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [activeTab, setActiveTab] = useState('GOOD');
  const [usnValue, setUsnValue] = useState('');
  const [goodScanned, setGoodScanned] = useState([]);
  const [damageScanned, setDamageScanned] = useState([]);
  const [staffText, setStaffText] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [reprinting, setReprinting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [mismatch, setMismatch] = useState(null);

  const focusUsn = useCallback(() => setTimeout(() => usnInputRef.current?.focus(), 80), []);
  const focusStaff = useCallback(() => setTimeout(() => staffInputRef.current?.focus(), 80), []);

  const fetchOrders = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await listJobOrders({ status: 'all', limit: 300 });
      setOrders((data.orders || []).filter((o) => OPEN_STATUSES.includes(o.status)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load job orders');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const resetScans = () => {
    setGoodScanned([]); setDamageScanned([]); setActiveTab('GOOD');
    setUsnValue(''); setStaffText(''); setRemarks('');
  };

  const openOrder = async (jwId) => {
    setLoadingDetail(true);
    setLastResult(null);
    try {
      const data = await getJobOrder(jwId);
      setSelected(data.order);
      setReturns(data.returns || []);
      const bu = await getBundleUsns(jwId);
      setSentGroups(bu.groups || []);
      resetScans();
      focusStaff();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Job order not found');
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeOrder = () => {
    setSelected(null);
    setSentGroups([]);
    resetScans();
  };

  // ----- Derived -----
  const allUsns = useMemo(() => {
    const out = [];
    sentGroups.forEach((g) => (g.usns || []).forEach((u) => { if (u && !out.includes(u)) out.push(u); }));
    return out;
  }, [sentGroups]);
  const usnSortMap = useMemo(() => {
    const m = {};
    sentGroups.forEach((g) => (g.usns || []).forEach((u) => { if (u && !(u in m)) m[u] = g.sort_no || ''; }));
    return m;
  }, [sentGroups]);

  const alreadyReturned = useMemo(() => new Set(selected?.returned_usns || []), [selected]);
  const goodSet = useMemo(() => new Set(goodScanned), [goodScanned]);
  const damageSet = useMemo(() => new Set(damageScanned), [damageScanned]);
  const staffIds = useMemo(() => parseStaff(staffText), [staffText]);
  const hasStaff = staffIds.length > 0;

  const remaining = useMemo(
    () => allUsns.filter((u) => !alreadyReturned.has(u) && !goodSet.has(u) && !damageSet.has(u)),
    [allUsns, alreadyReturned, goodSet, damageSet]
  );

  const sentQty = selected?.sent_qty || 0;
  const priorAccounted = (selected?.returned_good_qty || 0) + (selected?.returned_damage_qty || 0);
  const scannedThis = goodScanned.length + damageScanned.length;
  const accountedAfter = priorAccounted + scannedThis;
  const balanceAfter = Math.max(0, sentQty - accountedAfter);
  // LATE USN BIND: sent bundles carry ZERO upstream USNs, so progress is
  // qty-driven (against sent_qty), not against a known sent-USN list.
  const isLegacyUsnOrder = allUsns.length > 0;
  const notScannedQty = Math.max(0, sentQty - accountedAfter);

  const canSubmit = selected && scannedThis > 0 && hasStaff && !submitting;

  // ----- Scan (bind a pre-printed pool sticker) -----
  const handleScan = async (e) => {
    e?.preventDefault?.();
    const code = usnValue.trim();
    if (!code || scanning) return;
    if (!hasStaff) {
      toast.error('Scan a Staff ID first — returned pieces must be credited for wages');
      focusStaff();
      return;
    }
    if (alreadyReturned.has(code)) {
      toast.error(`USN ${code} was already returned in an earlier receipt`);
      setUsnValue(''); focusUsn(); return;
    }
    if (goodSet.has(code)) {
      toast.error(`USN ${code} already scanned into GOOD`);
      setUsnValue(''); focusUsn(); return;
    }
    if (damageSet.has(code)) {
      toast.error(`USN ${code} already scanned into DAMAGE`);
      setUsnValue(''); focusUsn(); return;
    }
    setScanning(true);
    try {
      await apiClient.post('/segregation/validate-pool-usn', { usn_code: code });
      if (activeTab === 'GOOD') setGoodScanned((p) => [code, ...p]);
      else setDamageScanned((p) => [code, ...p]);
    } catch (err) {
      toast.error(err.response?.data?.detail || `USN ${code} is not a valid pool sticker`);
    } finally {
      setScanning(false);
      setUsnValue(''); focusUsn();
    }
  };

  const removeScanned = (code) => {
    setGoodScanned((p) => p.filter((u) => u !== code));
    setDamageScanned((p) => p.filter((u) => u !== code));
  };

  // ----- Print -----
  const doPrint = async (list, mode) => {
    if (!list.length) { toast.error('Nothing to print'); return; }
    const setBusy = mode === 'all' ? setPrinting : setReprinting;
    setBusy(true);
    try {
      const sortNos = list.map((u) => usnSortMap[u] || '');
      const n = await print.printUsns(list, { sortNos });
      toast.success(`${n} USN sticker${n !== 1 ? 's' : ''} sent to ${print.printDestination}`);
      focusUsn();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Print failed');
    } finally {
      setBusy(false);
    }
  };

  // ----- Submit -----
  const doSubmit = async (reason) => {
    setSubmitting(true);
    try {
      const res = await returnJobOrder(selected.jw_id, {
        good_usns: goodScanned,
        damage_usns: damageScanned,
        remarks: (reason ?? remarks).trim(),
        staff_ids: parseStaff(staffText),
      });
      setLastResult(res);
      toast.success(`Return recorded — ${res.good_qty} good, ${res.damage_qty} damage${res.override_used ? ' (override)' : ''}`);
      setOverrideOpen(false);
      setOverrideReason('');
      setMismatch(null);
      closeOrder();
      fetchOrders();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && detail.code === 'COUNT_MISMATCH') {
        setMismatch(detail);
        setOverrideOpen(true);
        if (reason) toast.error(detail.message || 'Excess return — override not accepted');
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to record return');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    void doSubmit(remarks);
  };

  const isGood = activeTab === 'GOOD';
  const currentScanned = isGood ? goodScanned : damageScanned;

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="job-worker-return-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/sections/inhouse')}
              className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Factory className="w-7 h-7" />
              <div>
                <h1 className="text-xl font-bold">Job Worker Return</h1>
                <p className="text-sm text-[#E8DCC8]">Scan returned pieces into Good / Damage against a job order</p>
              </div>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/sections/inhouse/jobber-report')}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="go-report-btn">
            <FileBarChart className="w-4 h-4 mr-2" /> Report
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {lastResult && (
          <Card className="border-green-200 bg-green-50/40" data-testid="return-summary-card">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-bold text-green-800">Return Recorded — {lastResult.return_id}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div><span className="text-gray-500">Jobber:</span> {lastResult.jobber_name}</div>
                <div><span className="text-gray-500">Good:</span> <span className="font-semibold text-emerald-700">{lastResult.good_qty}</span></div>
                <div><span className="text-gray-500">Damage:</span> <span className="font-semibold text-amber-700">{lastResult.damage_qty}</span></div>
                <div><span className="text-gray-500">Balance:</span> <span className="font-semibold">{lastResult.balance_qty}</span></div>
              </div>
              <p className="mt-3 text-xs text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2">
                {lastResult.skip_pressing
                  ? 'Good pieces are marked PRESSED and available for Assortment Packing. Damage pieces are queued in the Damage op.'
                  : 'Good pieces are queued for the Pressing op. After pressing they become available for Assortment Packing. Damage pieces are queued in the Damage op.'}
              </p>
            </CardContent>
          </Card>
        )}

        {selected ? (
          <>
            {/* Order info + tally */}
            <Card className="border-indigo-200" data-testid="return-panel">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Factory className="w-5 h-5 text-indigo-600" /> {selected.jobber_name || 'Job Worker'}
                    </CardTitle>
                    <p className="text-xs text-gray-500">
                      <span className="font-mono">{selected.jw_id}</span> · {selected.target?.design_name || '—'}
                      {selected.target?.shade ? ` · ${selected.target.shade}` : ''}
                      {selected.target?.sku_code ? ` · ${selected.target.sku_code}` : ''}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={closeOrder}
                    className="text-gray-500 shrink-0" data-testid="change-order-btn">
                    <X className="w-4 h-4 mr-1" /> Change
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-indigo-50 rounded p-2"><p className="text-[10px] text-indigo-600">Sent</p><p className="font-bold text-indigo-800" data-testid="sent-qty">{sentQty}</p></div>
                  <div className="bg-emerald-50 rounded p-2"><p className="text-[10px] text-emerald-600">Good (prior)</p><p className="font-bold text-emerald-700">{selected.returned_good_qty || 0}</p></div>
                  <div className="bg-amber-50 rounded p-2"><p className="text-[10px] text-amber-600">Damage (prior)</p><p className="font-bold text-amber-700">{selected.returned_damage_qty || 0}</p></div>
                  <div className="bg-gray-50 rounded p-2"><p className="text-[10px] text-gray-500">Balance after</p><p className="font-bold" data-testid="balance-after">{balanceAfter}</p></div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">This return</span>
                    <span className="font-semibold" data-testid="scan-progress">{scannedThis} scanned · {notScannedQty} not scanned back</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-lime-500 transition-all"
                      style={{ width: `${sentQty ? Math.round((accountedAfter / sentQty) * 100) : 0}%` }} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Staff scan — REQUIRED before capturing pieces (wage credit) */}
            <Card className={hasStaff ? 'border-emerald-200' : 'border-rose-300 bg-rose-50/30'} data-testid="staff-card">
              <CardContent className="pt-4 space-y-2">
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <ScanBarcode className="w-4 h-4 text-indigo-600" />
                  Staff ID(s) — scan before capturing pieces <span className="text-rose-600">*</span>
                </Label>
                <Input ref={staffInputRef} value={staffText} onChange={(e) => setStaffText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focusUsn(); } }}
                  placeholder="Scan / type Staff ID (comma-separated), e.g. V0104, S0812" data-testid="staff-input" />
                {hasStaff ? (
                  <div className="flex flex-wrap gap-1.5">
                    {staffIds.map((s) => (
                      <Badge key={s} variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-mono" data-testid={`staff-chip-${s}`}>{s}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-rose-600 flex items-center gap-1" data-testid="staff-required-hint">
                    <AlertTriangle className="w-3 h-3" /> Required — enter the staff who worked these pieces before scanning USNs.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Print controls — legacy only (late-bind pieces use pre-printed pool stickers) */}
            {isLegacyUsnOrder && (
            <Card>
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-xs text-gray-500">Reprint the original USN stickers for pieces whose label is missing / damaged</span>
                  <div className="flex items-center gap-2">
                    <PrintMethodToggle screenKey="inhouse-job-order" autoNetworkWhenRouted onMethodChange={print.setPrintMethod} />
                    <Button size="sm" variant={print.ready ? 'outline' : 'default'}
                      className={print.ready ? '' : 'bg-amber-500 hover:bg-amber-600 text-white'}
                      onClick={() => print.setSettingsOpen(true)} data-testid="printer-settings-btn">
                      <Settings className="w-4 h-4 mr-1.5" /> {print.ready ? 'Printer' : 'Setup'}
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    onClick={() => doPrint(allUsns, 'all')} disabled={printing || reprinting || !allUsns.length}
                    data-testid="print-all-btn">
                    <Printer className={`w-4 h-4 mr-2 ${printing ? 'animate-pulse' : ''}`} />
                    {printing ? 'Printing…' : `Print All Stickers (${allUsns.length})`}
                  </Button>
                  <Button variant="outline" onClick={() => doPrint(remaining, 'unscanned')}
                    disabled={printing || reprinting || remaining.length === 0} data-testid="print-unscanned-btn">
                    <Printer className={`w-4 h-4 mr-2 ${reprinting ? 'animate-pulse' : ''}`} />
                    {reprinting ? 'Printing…' : `Print Unscanned (${remaining.length})`}
                  </Button>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Tabs */}
            <div className="grid grid-cols-2 gap-2">
              <Button variant={isGood ? 'default' : 'outline'}
                className={isGood ? 'bg-green-600 hover:bg-green-700' : ''}
                onClick={() => { setActiveTab('GOOD'); focusUsn(); }} data-testid="tab-good">
                <ThumbsUp className="w-4 h-4 mr-2" /> Good ({goodScanned.length})
              </Button>
              <Button variant={!isGood ? 'default' : 'outline'}
                className={!isGood ? 'bg-amber-600 hover:bg-amber-700' : ''}
                onClick={() => { setActiveTab('DAMAGE'); focusUsn(); }} data-testid="tab-damage">
                <ShieldAlert className="w-4 h-4 mr-2" /> Damage ({damageScanned.length})
              </Button>
            </div>

            {/* Scan input */}
            <Card className={isGood ? 'border-green-200' : 'border-amber-200'}>
              <CardContent className="pt-4">
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Scan pre-printed USN sticker into <span className={isGood ? 'text-green-700 font-bold' : 'text-amber-700 font-bold'}>{activeTab}</span>
                </label>
                <form onSubmit={handleScan} className="flex gap-2">
                  <Input ref={usnInputRef} value={usnValue} onChange={(e) => setUsnValue(e.target.value)}
                    placeholder={hasStaff ? 'Scan USN barcode…' : 'Scan a Staff ID above first…'}
                    disabled={!hasStaff || scanning} data-testid="usn-scan-input" />
                  <Button type="submit" disabled={!hasStaff || !usnValue.trim() || scanning} data-testid="usn-scan-submit">
                    <ScanBarcode className="w-4 h-4" />
                  </Button>
                </form>
                {remaining.length > 0 ? (
                  <p className="text-xs text-gray-400 mt-1.5">{remaining.length} piece{remaining.length !== 1 ? 's' : ''} not scanned back yet (shortage stays as balance).</p>
                ) : (
                  <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> All sent pieces accounted for.</p>
                )}
              </CardContent>
            </Card>

            {/* Current tab list */}
            {currentScanned.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Package className="w-4 h-4" /> {activeTab} ({currentScanned.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1.5 max-h-72 overflow-y-auto">
                    {currentScanned.map((code) => (
                      <div key={code} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5 text-sm" data-testid={`queued-usn-${code}`}>
                        <div className="flex items-center gap-2">
                          <ScanBarcode className={`w-3.5 h-3.5 ${isGood ? 'text-green-500' : 'text-amber-500'}`} />
                          <span className="font-mono font-medium">{code}</span>
                        </div>
                        <Button variant="ghost" size="sm" className="text-gray-400 hover:text-red-600" onClick={() => removeScanned(code)} data-testid={`queued-delete-${code}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Not-scanned hint */}
            {remaining.length > 0 && (
              <Card className="border-amber-200 bg-amber-50/40" data-testid="unscanned-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-4 h-4" /> Not scanned yet ({remaining.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                    {remaining.map((code) => (
                      <span key={code} className="font-mono text-xs bg-white border border-amber-200 rounded px-2 py-0.5 text-amber-800" data-testid={`unscanned-usn-${code}`}>{code}</span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Remarks + submit */}
            <Card>
              <CardContent className="pt-4 space-y-3">
                <div>
                  <Label className="text-xs text-gray-500">Remarks (optional)</Label>
                  <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2}
                    placeholder="Any note…" className="mt-1" data-testid="remarks-input" />
                </div>
                <Button className="w-full bg-lime-600 hover:bg-lime-700" size="lg"
                  onClick={submit} disabled={!canSubmit} data-testid="return-submit-btn">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                    : !hasStaff ? 'Scan a Staff ID first'
                    : scannedThis === 0 ? 'Scan at least one returned piece'
                    : `Record Return — ${goodScanned.length} good · ${damageScanned.length} damage`}
                </Button>
              </CardContent>
            </Card>

            {returns.length > 0 && (
              <Card data-testid="return-history-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <History className="w-5 h-5 text-indigo-600" /> Return History ({returns.length})
                  </CardTitle>
                  <p className="text-xs text-gray-500">Past return receipts for this job order</p>
                </CardHeader>
                <CardContent className="space-y-2">
                  {returns.slice().reverse().map((r) => (
                    <div key={r.return_id} className="border rounded-lg px-3 py-2 text-sm" data-testid={`return-history-${r.return_id}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-mono text-xs font-semibold text-gray-800">{r.return_id}</span>
                        <span className="text-[11px] text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleString('en-IN') : '—'}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap text-xs">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200"><ThumbsUp className="w-3 h-3 mr-1" /> Good {r.good_qty}</Badge>
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200"><ShieldAlert className="w-3 h-3 mr-1" /> Damage {r.damage_qty}</Badge>
                        <span className="text-gray-500">by {(r.staff || []).map((s) => s.staff_name || s.staff_id).join(', ') || r.created_by_name || '—'}</span>
                      </div>
                      {r.remarks && <p className="text-[11px] text-gray-500 mt-1">Remarks: {r.remarks}</p>}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="w-5 h-5 text-indigo-600" /> Open Job Orders
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={fetchOrders} disabled={loadingList} data-testid="refresh-orders-btn">
                  <RefreshCw className={`w-4 h-4 ${loadingList ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <p className="text-xs text-gray-500">Pick an OPEN / RECEIVING order, then scan the returned pieces</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {loadingList ? (
                <p className="text-sm text-gray-500 py-6 text-center" data-testid="list-loading">Loading…</p>
              ) : orders.length === 0 ? (
                <div className="text-sm text-gray-500 py-8 text-center" data-testid="empty-list">
                  <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  No open job orders. <button className="text-indigo-600 underline" onClick={() => navigate('/sections/inhouse/create-job-order')}>Create one</button>.
                </div>
              ) : orders.map((o) => (
                <button key={o.jw_id} onClick={() => openOrder(o.jw_id)} disabled={loadingDetail}
                  className="w-full text-left border border-gray-200 rounded-lg px-3 py-2.5 hover:border-indigo-300 hover:bg-indigo-50/40 transition flex items-center gap-3"
                  data-testid={`order-${o.jw_id}`}>
                  <Factory className="w-5 h-5 text-indigo-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{o.jobber_name || '—'}</p>
                    <p className="text-xs text-gray-500 font-mono truncate">{o.jw_id} · {o.target?.design_name || '—'}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-lg font-bold text-indigo-700">{o.balance_qty}</p>
                    <p className="text-[10px] text-gray-500">bal / {o.sent_qty} sent</p>
                  </div>
                  <Badge variant="outline" className={o.status === 'RECEIVING' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>{o.status}</Badge>
                </button>
              ))}
            </CardContent>
          </Card>
        )}
      </main>

      <UsnPrinterSettingsDialog open={print.settingsOpen} onOpenChange={print.setSettingsOpen} print={print} />

      {/* Admin override (excess return beyond sent qty) */}
      <Dialog open={overrideOpen} onOpenChange={(o) => { setOverrideOpen(o); if (!o) setOverrideReason(''); }}>
        <DialogContent data-testid="jwr-override-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-amber-600" /> Excess return — Admin override
            </DialogTitle>
            <DialogDescription>
              {mismatch?.message || 'The returned quantity exceeds what was sent.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="flex gap-3 text-sm">
              <span className="px-2 py-1 rounded bg-gray-100" data-testid="jwr-override-expected">Sent: <b>{mismatch?.expected ?? sentQty}</b></span>
              <span className="px-2 py-1 rounded bg-gray-100" data-testid="jwr-override-scanned">Returned: <b>{mismatch?.scanned ?? accountedAfter}</b></span>
            </div>
            {isAdminRole() ? (
              <>
                <Label className="text-sm font-medium text-gray-700">Reason (required)</Label>
                <Textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Why is the returned quantity more than what was sent?" rows={3}
                  data-testid="jwr-override-reason" />
              </>
            ) : (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="jwr-override-not-admin">
                An Admin must approve this excess. Ask an Admin to submit, or correct the scanned count.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOverrideOpen(false); setOverrideReason(''); }} data-testid="jwr-override-cancel">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700"
              disabled={!isAdminRole() || !overrideReason.trim() || submitting}
              onClick={() => doSubmit(overrideReason.trim())} data-testid="jwr-override-confirm">
              {submitting ? 'Submitting…' : 'Approve & Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobWorkerReturn;
